import { Base, StrictBase } from '@wilson/base';
import type { AbsenceWithCategory } from '../absences/absence';
import type { Shift } from '../shifts/shift';
import { WithTranslations } from '../translations';

export interface CorePayrollCategory extends Partial<WithTranslations>, Base {
  name: string;
  translatedName: string;
}

export interface PayrollTransaction {
  id: string;
  transactionValue: number;
  transactionStatus: string;
  transactionDate: string;
  payrollCategoryId: string;
  payrollCategory?: CorePayrollCategory;
  userId: string;
  shift?: Shift;
  shiftId: string | null;
  absence?: AbsenceWithCategory;
  absenceId: string | null;
  createdByUserId: string | null;
}

export interface OvertimeCompensationTransaction {
  transactionValue: number;
  transactionDate: string;
  userId: string;
}

export interface PayrollProvider extends Base {
  name: string;
}

export interface PayrollWorkflow extends StrictBase, WithTranslations {
  url: string;
  name: string;
  organizationalUnitId: string;
}

export enum PayrollCategoryName {
  ExtraHolidayHours = 'extra-holiday-hours',
  Holiday = 'holiday',
  HolidayBonus = 'holiday-bonus',
  IsWorkTime = 'is-work-time',
  TravelBonus = 'travel-bonus',
  StandbyBonus = 'standby-bonus',
  Etv1Bonus = 'etv1-bonus',
  SpontaneousShiftBonus = 'spontaneous-shift-bonus',
  OvernightStayBonus = 'overnight-stay-bonus',
  NightHours = 'night-hours',
  OvernightShiftBonus = 'overnight-shift-bonus',
  OvertimeHoursWeekday = 'overtime-hours-weekday',
  OvertimeRate = 'overtime-rate',
  TotalOvertimeRate = 'total-overtime-rate',
  OvertimeHoursHoliday = 'overtime-hours-holiday',
  OvertimeHoursNight = 'overtime-hours-night',
  OvertimeHoursSunday = 'overtime-hours-sunday',
  OvertimeCompensation = 'overtime-compensation',
  SaturdayHours = 'saturday-hours',
  SplitShiftBonus = 'split-shift-bonus',
  SundayBonus = 'sunday-bonus',
  SickDay = 'sick-day',
  PlannedWorktime = 'planned-worktime',
  PaidVacationDay = 'paid-vacation-day',
  PerDiem = 'per-diem',
}

export enum PayrollProviderName {
  Datev = 'datev',
}

export enum PayrollProviderDisplayName {
  Datev = 'DATEV Lodas',
}

export enum PayrollProviderId {
  Csv = '556fb935-91a7-45cd-a4cb-6103f8cc69f8',
  Datev = 'f547b02b-858b-4016-872d-9687dd5b2a36',
  SapHR = 'd93535cd-4c43-46ee-9a6f-75ba99150f2d',
  Sage = '70535659-2915-4e0f-b990-f43088e64e4c',
  Lexware = '47470b1a-c6b7-42b2-8f0f-0c0256cce708',
  DatevLohnUndGehalt = '2a01722d-0595-4ef1-bd25-c3226c90cfc0',
  Varial = 'b01cacd6-048d-435d-80a3-ac18a106bead',
}

export enum PayrollTransactionStatus {
  Final = 'final',
  Temporary = 'temporary',
}

export enum ShiftPayrollAcceptanceStatus {
  PartlySubmittedToPayroll = 'partly-submitted-to-payroll',
  ReportingMissing = 'reporting-missing',
  SubmittedToPayroll = 'submitted-to-payroll',
  WorkTimeCheckComplete = 'work-time-check-complete',
  WorkTimeCheckOpen = 'work-time-check-open',
}

export enum PayrollUnitName {
  Eur = 'eur',
  Hour = 'hour',
  Minute = 'minute',
  Kilometer = 'kilometer',
  Week = 'week',
  Month = 'month',
  Day = 'day',
  Year = 'year',
  Gbp = 'gbp',
}

export enum PayrollRecalculationType {
  Shift = 'shift',
  Absence = 'absence',
  PlannedWorkTime = 'planned-work-time',
}

export interface PayrollRecalculationRequest {
  userIds: string[];
  from: Date;
  to: Date;
  recalculateFor: PayrollRecalculationType[];
}
